import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { FileSpreadsheet, Folder, FolderOpen, Images, Plus } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { kebabCase } from 'lodash';
import { z } from 'zod';
import {
  type CsvMetadataFieldOption,
  type CsvWorkflowOption,
  useCsvMetadata,
} from '@/hooks/data/inspector/useCsvMetadata';
import { MetadataCheckbox } from '@/components/inspector/metadata-accordion/metadata-checkbox';
import { SelectionRow } from '@/components/inspector/rows/selection-row';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { useMetadataList } from '@/hooks/metadata/useMetadata';
import { useWorkflowList } from '@/hooks/workflow/useWorkflow';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { Label, LabelVariants } from '@/components/ui/label';
import { TaskState } from '@/types/uploads';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const UploadsAccordion = () => {
  const openModal = useOpenModal();
  const tUpload = useTranslations('upload');

  const { data: metadataFieldList } = useMetadataList(
    {
      pagination: {
        offset: null,
        limit: null,
      },
      sort: {
        value: SortOrderValues.MANUAL,
        order: SortOrderOptions.ASC,
      },
      queryString: '',
    },
    {
      enabled: true,
      staleTime: 5 * 60_000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchInterval: false,
    },
  );

  const { data: workflowFieldList } = useWorkflowList(
    {
      pagination: {
        offset: null,
        limit: null,
      },
      sort: {
        value: SortOrderValues.CREATED_AT,
        order: SortOrderOptions.ASC,
      },
      queryString: '',
    },
    { enabled: true },
  );

  const {
    getActiveUploadSession,
    setCsvFileName,
    toggleKeepExistingFolders,
    toggleReplaceAssetName,
    setCsvData,
    setUploadFolderPath,
    changeFolder,
    setSelectedAlbums,
  } = useUploadStore();

  const {
    session: { uploadFolderPath, csvFileName, uploadState, keepExistingFolders, replaceAssetName, selectedAlbums },
    uploadSessionId,
  } = getActiveUploadSession();

  // Keep the Upload Folder path in sync when a folder is renamed in the tree
  const rootFolders = useTreeStore((s) => s.rootFolders);
  const findNodePath = useTreeStore((s) => s.findNodePath);
  const reusableTreeSelectedFolder = useTreeStore((s) => s.reusableTreeSelectedFolder);
  const clearReusableTreeSelectedFolder = useTreeStore((s) => s.setReusableTreeSelectedFolder);

  useEffect(() => {
    // Only attempt sync when a folder is selected, uploads are idle, and a path is currently set
    if (!reusableTreeSelectedFolder || uploadState !== TaskState.NotStarted || uploadFolderPath === '') {
      return;
    }

    const namePath = findNodePath(reusableTreeSelectedFolder, 'name');
    const newPath = namePath && namePath.length > 0 ? `/${namePath.join('/')}` : '';

    if (newPath && newPath !== uploadFolderPath) {
      // Update stored path and adjust any existing grouped file paths
      changeFolder(uploadSessionId, uploadFolderPath, newPath);
      setUploadFolderPath(uploadSessionId, newPath);
    }
  }, [
    rootFolders,
    reusableTreeSelectedFolder,
    uploadFolderPath,
    uploadSessionId,
    changeFolder,
    setUploadFolderPath,
    uploadState,
    findNodePath,
  ]);

  // Keep selected album labels in sync when albums are renamed in the tree
  useEffect(() => {
    if (!selectedAlbums || selectedAlbums.length === 0 || uploadState !== TaskState.NotStarted) {
      return;
    }

    let changed = false;
    const updated = selectedAlbums.map((a) => {
      const namePath = findNodePath(a.id, 'name');
      const latest = namePath && namePath.length > 0 ? namePath[namePath.length - 1] : a.label;
      if (latest !== a.label) {
        changed = true;
      }
      return { id: a.id, label: latest };
    });

    if (changed) {
      setSelectedAlbums(uploadSessionId, updated);
    }
  }, [rootFolders, selectedAlbums, uploadState, uploadSessionId, setSelectedAlbums, findNodePath]);

  const workflowOptions: Array<CsvWorkflowOption> = useMemo(
    () => workflowFieldList.map((wf) => ({ slug: wf.slug, statusSlugs: Object.keys(wf.statuses) })),
    [workflowFieldList],
  );
  const csvMetadataDefinitions: Array<CsvMetadataFieldOption> = useMemo(
    () => metadataFieldList.map((f) => ({ slug: f.slug, type: f.type })),
    [metadataFieldList],
  );
  const { processCsvFile } = useCsvMetadata(uploadSessionId, workflowOptions, csvMetadataDefinitions);

  const formFields: Array<any> = useMemo(
    () => [
      {
        'name': 'Keep existing folders',
        'slug': 'keep-existing-folders',
        'description':
          'When adding a directory, the existing directory hierarchy will be preserved and assets will be imported in the selected folder inside new sub-folders matching the asset path relative to the added directory.',
        'type': 'boolean',
        'checked': keepExistingFolders,
        'data-cy': 'keep-existing-folders-switch',
        'disabled': uploadFolderPath === '',
        'onClick': () => {
          toggleKeepExistingFolders(uploadSessionId);
        },
      },
      {
        'name': 'Replace assets matching filename',
        'slug': 'replace-assets-matching-filename',
        'description':
          'When a file name matches an asset ID, upload as a new version of that asset and activate the new version.',
        'type': 'boolean',
        'checked': replaceAssetName,
        'data-cy': 'replace-asset-name-switch',
        'disabled': uploadFolderPath === '',
        'onClick': () => {
          toggleReplaceAssetName(uploadSessionId);
        },
      },
    ],
    [
      keepExistingFolders,
      replaceAssetName,
      openModal,
      uploadFolderPath,
      toggleKeepExistingFolders,
      toggleReplaceAssetName,
      csvFileName,
      uploadState,
      metadataFieldList,
    ],
  );

  const formSchema = z.object(
    formFields.reduce(
      (acc, field) => {
        if (field.validation) {
          acc[field.slug] = field.validation;
        }
        return acc;
      },
      {} as Record<string, z.ZodType<any>>,
    ),
  );

  const defaultValues = formFields.reduce(
    (acc, field) => {
      acc[field.slug] = field.value;
      return acc;
    },
    {} as Record<string, any>,
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    form.setValue('upload-folder', uploadFolderPath);
    form.setValue('keep-existing-folders', keepExistingFolders);
    form.setValue('replace-assets-matching-filename', replaceAssetName);
    form.setValue('metadata-csv', csvFileName);
    form.setValue('albums', selectedAlbums ?? [], { shouldDirty: false });
  }, [csvFileName, form, keepExistingFolders, replaceAssetName, uploadFolderPath, selectedAlbums]);

  const asArray = <T,>(v: T | Array<T> | null | undefined): Array<T> =>
    v === null || v === undefined ? [] : Array.isArray(v) ? v : [v];

  const nonEmpty = (arr: Array<any>) =>
    arr.filter((v) => (typeof v === 'string' ? v.trim() : String(v?.label ?? v?.id ?? '').trim()).length > 0);

  const uploadFolder = form.watch('upload-folder') as string | undefined;
  const albums = form.watch('albums') as Array<{ id: string; label: string }> | undefined;
  const csv = form.watch('metadata-csv') as string | undefined;

  return (
    <FormProvider {...form}>
      <form className="flex flex-col space-y-10 p-6">
        <SelectionRow
          autoFocus={true}
          title={tUpload('uploadFolder')}
          required
          descriptionEmpty={tUpload('noFolderSelected')}
          actionKind="open-tree"
          actionIcon={FolderOpen}
          icons={Folder}
          iconsClassName="stroke-folder-color"
          disabled={uploadState !== TaskState.NotStarted}
          onOpenDialog={() => openModal(ModalId.IngestionFolderSelect)}
          values={nonEmpty(asArray(uploadFolder))}
          onRemove={() => {
            form.setValue('upload-folder', '', { shouldDirty: true });
            setUploadFolderPath(uploadSessionId, '');
            changeFolder(uploadSessionId, uploadFolderPath, ``);
            clearReusableTreeSelectedFolder(undefined);

            // Reset CSV file when upload folder is removed
            if (form.getValues('metadata-csv')) {
              form.setValue('metadata-csv', '', { shouldDirty: true });
              setCsvFileName(uploadSessionId, '');
              setCsvData(uploadSessionId, {});
            }
          }}
          actionTooltip={tUpload('selectUploadFolder')}
        />

        <SelectionRow
          disabled={uploadFolderPath === '' && (form.getValues('albums') as Array<any> | undefined)?.length === 0}
          title={tUpload('addToAlbums')}
          descriptionEmpty={tUpload('noAlbumSelected')}
          actionKind="open-tree"
          actionIcon={Plus}
          icons={Images}
          iconsClassName="stroke-album-color"
          onOpenDialog={() => openModal(ModalId.IngestionAlbumSelect)}
          values={nonEmpty(asArray(albums))}
          actionTooltip={
            uploadFolderPath === ''
              ? tUpload.rich('selectFolderToEnableOption', {
                  bold: (chunks) => <span className="font-bold">{chunks}</span>,
                })
              : tUpload('selectAlbum')
          }
          onRemove={(id) => {
            const current = asArray(form.getValues('albums') as Array<any>);
            const next = current.filter((v: any) => (v.id ?? v) !== id);

            form.setValue('albums', next, { shouldDirty: true });
            useUploadStore.getState().setSelectedAlbums(uploadSessionId, next);
          }}
        />

        <div className="flex flex-col gap-y-1.5">
          <div>
            <Label
              variant={LabelVariants.INSPECTOR_SECTION}
              className={cn('text-text-gray', { 'opacity-25': uploadFolderPath === '' })}
            >
              {tUpload('settings')}
            </Label>
          </div>

          <div className="flex flex-col gap-y-2">
            {formFields.map((formField) => (
              <Controller
                key={formField.name}
                name={formField.name}
                control={form.control}
                render={({ field }) => (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col">
                      <div
                        className={cn('text-xs font-semibold text-text-base', {
                          'opacity-25': formField.disabled ?? false,
                        })}
                        data-cy={`${kebabCase(formField.name)}-label`}
                      >
                        {formField.name}
                      </div>
                    </div>
                    <MetadataCheckbox
                      id={formField.name}
                      name={formField.name}
                      checked={formField.checked}
                      value={formField.checked}
                      disabled={formField.disabled ?? false}
                      data-cy={formField.dataCy}
                      onClick={formField.onClick}
                      onChange={(state) => {
                        const next = state === true;
                        field.onChange(next);
                        formField.onToggle?.(next);
                      }}
                    />
                  </div>
                )}
              />
            ))}
          </div>
        </div>

        <SelectionRow
          disabled={uploadFolderPath === ''}
          title={tUpload('metadataCsv')}
          descriptionEmpty={tUpload('noFileSelected')}
          actionIcon={FolderOpen}
          actionKind="open-file"
          icons={FileSpreadsheet}
          iconsClassName="stroke-feature-workflow"
          fileAccept=".csv"
          values={nonEmpty(asArray(csv))}
          actionTooltip={
            uploadFolderPath === ''
              ? tUpload.rich('selectFolderToEnableOption', {
                  bold: (chunks) => <span className="font-bold">{chunks}</span>,
                })
              : tUpload('selectCsvFile')
          }
          onFilesChosen={async (files) => {
            const file = files[0];
            if (!file) {
              return;
            }
            await processCsvFile(file);
            form.setValue('metadata-csv', file.name);
            // Not sure if this is needed, since it's handled in processCsvFile
            // setCsvFileName(uploadSessionId, file.name);
          }}
          onRemove={() => {
            openModal(ModalId.Confirm, {
              content: tUpload('removeCsvConfirmation'),
              onConfirm: () => {
                setCsvFileName(uploadSessionId, '');
                setCsvData(uploadSessionId, {});
                form.setValue('metadata-csv', '');
              },
              confirmButtonDataCy: 'csv-dialog-confirm-continue',
              dismissButtonDataCy: 'csv-dialog-confirm-dismiss',
            });
          }}
        />
      </form>
    </FormProvider>
  );
};
